import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Theme for button colors
const theme = {
  white: {
    default: '#DFBD69',  
    hover: '#b7954d', 
  },
  grey: {
    default: '#DFBD69', 
    hover: '#b7954d',
  },
};

// Button styling
const Button = styled.button`
  background-color: ${(props) =>
    props.selected ? '#d3f8d3' : theme[props.theme].default};
  color: ${(props) => (props.theme === 'white' ? '#000000' : '#333333')};
  padding: 10px 18px;
  border-radius: ${(props) => props.radius || '10px'};
  outline: 0;
  border: 1px solid rgba(223,189,105,0.12);
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.2);
  transition: background-color 250ms ease;
  margin: 10px;
  width: ${(props) => props.width || 'auto'};

  &:hover {
    background-color: ${(props) =>
      props.selected ? '#a7d8a7' : theme[props.theme].hover};
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
    background-color: grey; /* Set background color to black for disabled state */
    color: #ffffff; /* Set text color to white for contrast */
  }
`;

Button.defaultProps = {
  theme: 'grey',
  radius: '10px',
  width: 'auto',
};


// Container styling
const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 10px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
  min-height: 100vh;
  background-color: #171520;
  color: #fff2d2;
  font-family: 'DMSans', Arial, sans-serif;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 10px;
  }
`;

// Panel styling
const Panel = styled.div`
  padding: 20px;
  border-radius: 10px;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 20px;
  
  &.leftpanel {
    background-color: rgba(10,3,30,0.35);
    border-right: 1px solid rgba(201,168,75,0.12);

    @media (max-width: 768px) {
      border-right: none;
    }
  }

  &.rightpanel {
    background-color: transparent;

    @media (max-width: 768px) {
      border-right: none;
      border-radius: 0;
    }
  }
`;

// Image styling for Master profile
const PanelImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 2px solid rgba(223,189,105,0.3);
  margin-bottom: 10px;
`;

// Styling for the custom calendar
const CalendarContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  border: 1px solid #DFBD69;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 20px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    gap: 1px;
    border-radius: 6px;
  }
  @media (max-width: 480px) {
    gap: 1px;
    border-width: 1px;
    border-radius: 6px;
  }
`;

// ...existing code...
const DayCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  /* Mobile-specific enhancements are applied in media queries below */
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  background-color: ${(props) => {
    if (props.disabled) return '#333'; // dark grey for disabled
    if (props.selected) return '#DFBD69'; // gold for selected
    return '#000000'; // black background
  }};

  color: ${(props) => {
    if (props.disabled) return '#666';
    if (props.selected) return '#000'; // black text for selected
    return '#DFBD69'; // gold font
  }};

  border: 2px solid
    ${(props) => (props.selected ? '#DFBD69' : '#DFBD69')};

  ${(props) =>
    props.selected &&
    `
    box-shadow: 0 0 0 2px #b7954d;
    font-weight: bold;
    transform: scale(1.08);
  `}

  &:hover {
    background-color: ${(props) =>
      props.disabled ? '#333' : props.selected ? '#DFBD69' : '#111111'};
    color: ${(props) =>
      props.disabled ? '#666' : props.selected ? '#000' : '#DFBD69'};
  }

  @media (max-width: 768px) {
    /* Keep cells square and touch-friendly on mobile */
    aspect-ratio: 1 / 1;
    font-size: 12px;
    padding: 6px; /* minimal padding; aspect-ratio enforces height */
    border-width: 1px;
    min-height: 40px; /* ensure adequate tap target on older browsers */
  }
  @media (max-width: 480px) {
    font-size: 12px;
    padding: 4px;
    border-width: 1px;
    min-height: 38px;
  }
`;
// ...existing code...



const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background-color: #DFBD69;
  font-weight: bold;
  color: #000;
  user-select: none;
  line-height: 1;

  @media (max-width: 768px) {
    padding: 8px;
    font-size: 12px;
  }
  @media (max-width: 480px) {
    padding: 6px;
    font-size: 12px;
  }
`;

// Month and year selection styling
const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;

  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 8px;
  }
  @media (max-width: 480px) {
    gap: 6px;
    margin-bottom: 6px;
  }
`;

const HeaderButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #DFBD69; /* Gold color */
  &:hover {
    color: #9e8500; /* Slightly lighter gold on hover, optional */
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 6px; /* improve touch target on mobile */
  }
  @media (max-width: 480px) {
    font-size: 14px;
    padding: 4px 6px;
  }
`;


const MonthYearSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MonthYearText = styled.span`
  font-weight: bold;
  font-size: 16px;
  line-height: 1;
  @media (max-width: 768px) {
    font-size: 14px;
  }
  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

function SecondPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedMaster } = location.state || {};
  const [selectedDate, setSelectedDate] = useState('');
  const ID = selectedMaster?.id || null;
  const [holidayDates, setHolidayDates] = useState([]);
  const { t, i18n } = useTranslation();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem('language') || 'en'
  );

  const daysOfWeek = i18n.language === 'id' 
  ? ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'] // Indonesian
  : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; // English
  
  const handleDateChange = (day) => {
    if (day) {
      const year = day.getFullYear();
      const month = String(day.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed, so we add 1
      const date = String(day.getDate()).padStart(2, '0');
      
      // Format as 'YYYY-MM-DD'
      const formattedDate = `${year}-${month}-${date}`;
      
      setSelectedDate(formattedDate); // Set the formatted date as a string
      console.log("Selected Date (formatted):", formattedDate); // Log or handle as needed
    }
  };

  const checkLoginAndOrder = () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (isLoggedIn === 'true') {
      console.log(selectedMaster);
      navigate('/schedule2', {
        state: { selectedMaster }
      });
    } else {
      navigate('/login', { state: { message: 'You need to log in before placing an order.' } });
    }
  };

  const handleConfirm = () => {
    if (selectedMaster && selectedDate) {
      checkLoginAndOrder();
      navigate('/schedule3', {
        state: {
          selectedMaster,
          selectedDate
        }
      });
      console.log("Selected Master:", selectedMaster);
      console.log("Selected Date:", selectedDate); // Log formatted date string
    } else {
      console.error("Either selectedMaster or selectedDate is missing");
    }
  };

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [apiData, setApiData] = useState([]);

  useEffect(() => {
    fetchApiData();
    fetchHolidayDates();
  }, [ID, currentDate]);

  const fetchApiData = async () => {
    try {
      const response = await fetch(`https://xianhefairy.com/LotusFairy_API/api/Booking/getBL?ID=${ID}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setApiData(data); // Set the fetched data
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Function to check if the date is today
  const isToday = (date) => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const fetchHolidayDates = async () => {
    if (!ID) return;
    const currDate = `${String(currentDate.getMonth() + 1).padStart(2, '0')}-${currentDate.getFullYear()}`; // API expects MM-YYYY
    try {
      const response = await fetch(`https://xianhefairy.com/LotusFairy_API/api/Booking/GetHolidayDate?MasterId=${ID}&CurrDate=${currDate}`);
      if (!response.ok) throw new Error('Holiday API error');
      const data = await response.json();
      // Convert LeaveDate from 'DD-MM-YYYY' to 'YYYY-MM-DD'
      const formattedDates = data.map(item => {
        const [day, month, year] = item.LeaveDate.split('-');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      });
      setHolidayDates(formattedDates);
    } catch (error) {
      console.error('Error fetching holiday dates:', error);
    }
  };

  const isDateDisabled = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    // Disable if in apiData or holidayDates
    return (
      apiData.some(item => item.DATE_SPEC === formattedDate) ||
      holidayDates.includes(formattedDate)
    );
  };
  
  const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to 00:00:00
    return date < today;
  };
    
  const handleMonthChange = (offset) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() + offset);
      return newDate;
    });
  };

  const handleYearChange = (offset) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setFullYear(prevDate.getFullYear() + offset);
      return newDate;
    });
  };

  const days = [];
  for (let i = startOfMonth.getDay(); i > 0; i--) {
    days.push(null);
  }
  for (let i = 1; i <= endOfMonth.getDate(); i++) {
    days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), i));
  }

  const handleDayClick = (day) => {
    // if (day && !isPastDate(day) && !isToday(day) && !isDateDisabled(day)) {
      if (day && !isPastDate(day) && !isDateDisabled(day)) {
      setSelectedDay(day);
      handleDateChange(day); // Pass the selected day to handleDateChange
    }
  };

  const goBack = () => {
    navigate(-1); // This navigates to the previous page
  };

  return (
    <Container>
      <Panel className="leftpanel">
        {selectedMaster && (
          <>
            <PanelImage src={selectedMaster.image} alt="Master" />
            <h2 style={{color:"#DFBD69"}}>{selectedMaster.name}</h2>
            <p style={{color:"#DFBD69"}}>{selectedMaster.description}</p>
          </>
        )}
      </Panel>
      <Panel className="rightpanel">

      <div style={{
          display: 'flex',
          justifyContent: 'flex-end', // Aligns the button to the right of the container
          padding: '20px',    
        }}>
          {/* <button onClick={goBack} style={{
            padding: '10px 20px',
            backgroundColor: '#DFBD69',
            color: 'black',
            border: '1px solid grey',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
          }}>
            Go Back
          </button> */}
        </div>
        
        <h4 style={{color:"#DFBD69"}}>{t('appointmentsPage.selectDate')}</h4>

        <Header>
          <HeaderButton onClick={() => handleMonthChange(-1)}>◄</HeaderButton>
          <MonthYearSelector>
            {/* <HeaderButton onClick={() => handleYearChange(-1)}>◄◄</HeaderButton> */}
            <MonthYearText style={{color:"#DFBD69"}}>
              {currentDate.toLocaleString(i18n.language === 'id' ? 'id-ID' : i18n.language, { month: 'long' })} {currentDate.getFullYear()}
            </MonthYearText>
            {/* <HeaderButton onClick={() => handleYearChange(1)}>►►</HeaderButton> */}
          </MonthYearSelector>
          <HeaderButton onClick={() => handleMonthChange(1)}>►</HeaderButton>
        </Header>
  
        <CalendarContainer>
          {daysOfWeek.map((dayOfWeek) => (
            <HeaderCell key={dayOfWeek}>{dayOfWeek}</HeaderCell>
          ))}
  
          {days.map((day, index) => (
            <DayCell
              key={day ? day.getTime() : `empty-${index}`}
              selected={selectedDay && day && day.toDateString() === selectedDay.toDateString()}
              // disabled={day && (isPastDate(day) || isDateDisabled(day))}
              disabled={day && (isPastDate(day) || isDateDisabled(day) || isToday(day))}
              onClick={() => day && handleDayClick(day)}
            >
              {day ? day.getDate() : ''}
            </DayCell>
          ))}
        </CalendarContainer>

        {/* New flex container for both buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          width: '100%',
        }}>
          <button onClick={goBack} style={{
            padding: '10px 20px',
            backgroundColor: '#DFBD69',
            color: 'black',
            border: '1px solid grey',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
          }}>
            {t('appointmentsPage.goBack')}
          </button>
          <Button onClick={handleConfirm} theme="white" radius="28px" width="220px">
            {t('appointmentsPage.confirmDate')}
          </Button>
        </div>
      </Panel>
    </Container>
  );
}

export default SecondPage;
